import classNames from 'classnames';
import Script from 'next/script';
import type { ReactNode } from 'react';

type IMainProps = {
  meta?: ReactNode;
  children: ReactNode;
  className?: string;
};

const urlSdk = process.env.NEXT_PUBLIC_URL_TRACKEDX ?? '';
const tokenSdk = process.env.NEXT_PUBLIC_TOKEN_SDK ?? '';

/** Query param an affiliate link lands with. `Affiliate.init` turns it into the `np_affiliate` cookie. */
const AFFILIATE_CLICK_ID_PARAM = 'clickId';

/**
 * The entry URL's click id, read on the module's first client evaluation — before the router can rewrite
 * the address bar.
 *
 * `Affiliate.init` calls `setCookie`, which reads `new URL(window.location.href).searchParams.get('clickId')`
 * and writes the attribution cookie from it, so it has to see the URL the visitor actually arrived on. While
 * the script ran during hydration that was a given. Behind `lazyOnload` it is not: a client-side navigation
 * can happen first and Next rewrites the address bar without the original query, and a click id lost there
 * is an affiliate commission that never gets attributed. `openai-ads-attribution.ts` keeps its own capture
 * outside the deferral gate for exactly this reason.
 */
const entryClickId = typeof window === 'undefined' ? null : new URL(window.location.href).searchParams.get(AFFILIATE_CLICK_ID_PARAM);

/**
 * How the SDK loads. A visit that arrives with a click id gets it during hydration, as before #1041: behind
 * load + idle, a visitor who leaves early (slow phone, the Discord invite, /trial redirecting to an external
 * LP) never runs `Affiliate.init`, the 30-day `np_affiliate` cookie is never written, and a purchase days
 * later credits no partner. Every other visit — the one PageSpeed measures — keeps it off the critical path.
 * `next/script` renders nothing for either strategy in the HTML, so server and client may differ here.
 */
const sdkStrategy = entryClickId ? 'afterInteractive' : 'lazyOnload';

/** Puts the entry click id back on the URL, so the SDK reads the same thing it read when it ran earlier. */
const restoreEntryClickId = () => {
  if (!entryClickId) return;

  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.get(AFFILIATE_CLICK_ID_PARAM)) return;

  currentUrl.searchParams.set(AFFILIATE_CLICK_ID_PARAM, entryClickId);
  // The existing history state is carried over: it is the router's own record of this entry, and replacing
  // it with null breaks the back button. The SDK itself does the same replaceState when it restores the id
  // from its cookie — this only makes sure there is an id for it to find on the first visit of a click.
  window.history.replaceState(window.history.state, '', currentUrl.href);
};

/**
 * TrackedX affiliate SDK. Rendered by `Main` on every page; exported on its own for pages that bring their own `<main>`
 * landmark (the home nova's `HomeNovaShell`) and so cannot be wrapped in `Main` without nesting two `<main>`.
 *
 * lazyOnload unless the visit carries a click id (`sdkStrategy` above): nothing on the first screen needs the
 * affiliate SDK, and at afterInteractive it was injected during hydration, inside the window Lighthouse charges
 * against LCP. Deferred, not removed: onReady still runs once the script has executed and re-fires on client
 * navigations, so `Affiliate.init` happens exactly as often as before, only later. The cookie banner this SDK
 * also draws is no longer a reason to hurry: `cookie-consent.tsx` (SCRUM-1954) renders the notice under its own
 * id and drops the SDK's `#cookie-consent`. The one thing the delay could have cost is the click id, which
 * `restoreEntryClickId` above hands back.
 */
const AffiliateSdkScript = () => (
  <Script
    src='/affiliates-sdk.min.js'
    strategy={sdkStrategy}
    onReady={() => {
      restoreEntryClickId();
      // @ts-ignore
      if (typeof window !== 'undefined' && window.Affiliate) {
        // @ts-ignore
        Affiliate.init(urlSdk, tokenSdk);
      }
    }}
  />
);

const Main = (props: IMainProps) => {
  return (
    <div className={classNames('w-full text-gray-700 antialiased', props.className)}>
      <AffiliateSdkScript />
      {props.meta}
      <main className='content'>{props.children}</main>
    </div>
  );
};

export { AffiliateSdkScript, Main };
